import React, { useMemo } from "react";
import { APIProvider, Map, useApiIsLoaded } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import MapCustomMarkers from "../MapCustomMarkers/MapCustomMarkers";
import { IconBox } from "@app/components/icons";
import { CircularProgress } from "@mui/material";

export type MarkedLocation = {
  key: string;
  location: google.maps.LatLngLiteral;
  iconUrl: string;
  checked: boolean;
};

interface MapViewProps {
  locations: MarkedLocation[] | null;
  ToolTipComponent: React.FC<{ selectedDataIndex?: number }>;
  ToolTipHeader: React.FC<{ selectedDataIndex: number }>;
  mapReloadkey: string;
  machinesExist: boolean;
}

const MapLoadingSpinner = () => <MapLoadingSpinnerContainer />;

const MapLoadingSpinnerContainer = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "450px",
    }}
  >
    <CircularProgress size={40} sx={{ color: "#140a9a" }} />
  </div>
);

const MapEmptyState = ({
  machinesExist,
  message,
}: {
  machinesExist: boolean;
  message: string;
}) => (
  <div
    className="empty-table bg-white flex flex-direction-column align-items-center justify-content-center"
    style={machinesExist ? { height: "450px", margin: 0 } : {}}
  >
    <div className="text-center">
      <IconBox />
      <p className="text-content">{message}</p>
    </div>
  </div>
);

type MapViewContentProps = Omit<MapViewProps, "locations"> & {
  locations: MarkedLocation[];
};

const MapViewContent: React.FC<MapViewContentProps> = ({
  locations,
  ToolTipComponent,
  ToolTipHeader,
  mapReloadkey,
}) => {
  const apiLoaded = useApiIsLoaded();

  const bounds = useMemo(() => {
    let minLat = locations[0].location.lat;
    let maxLat = locations[0].location.lat;
    let minLng = locations[0].location.lng;
    let maxLng = locations[0].location.lng;

    for (const loc of locations) {
      minLat = Math.min(minLat, loc.location.lat);
      maxLat = Math.max(maxLat, loc.location.lat);
      minLng = Math.min(minLng, loc.location.lng);
      maxLng = Math.max(maxLng, loc.location.lng);
    }

    return {
      south: minLat,
      west: minLng,
      north: maxLat,
      east: maxLng,
    } satisfies google.maps.LatLngBoundsLiteral;
  }, [locations]);

  if (!apiLoaded) {
    return <MapLoadingSpinner />;
  }

  return (
    <Map
      key={mapReloadkey}
      defaultBounds={locations.length === 1 ? undefined : bounds}
      defaultCenter={locations.length === 1 ? locations[0].location : undefined}
      defaultZoom={locations.length === 1 ? 17 : undefined}
      mapId="da37f3254c6a6d1c"
    >
      <MapCustomMarkers
        pois={locations}
        ToolTipComponent={ToolTipComponent}
        ToolTipHeader={ToolTipHeader}
      />
    </Map>
  );
};

const MapView: React.FC<MapViewProps> = ({
  locations,
  ToolTipComponent,
  ToolTipHeader,
  mapReloadkey,
  machinesExist,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { t } = useTranslation();

  if (!apiKey || !locations?.length) {
    return (
      <MapEmptyState
        machinesExist={machinesExist}
        message={t("mapView.NoMachineLocations")}
      />
    );
  }

  return (
    <APIProvider apiKey={apiKey}>
      <MapViewContent
        locations={locations}
        ToolTipComponent={ToolTipComponent}
        ToolTipHeader={ToolTipHeader}
        mapReloadkey={mapReloadkey}
        machinesExist={machinesExist}
      />
    </APIProvider>
  );
};

export default MapView;
